import { useState } from "react";
import { observer } from "mobx-react";
import { Button } from "reactstrap";
import { useStores } from "store/useStore";
import ConfirmDialog from "components/ConfirmDialog/ConfirmDialog";
import { toastError } from "domain/errorHandling/toaster";

/** Days an un-enrolled member may keep logging in after the requirement
 * flips on. After that the app blocks them into enrolment. */
export const MFA_GRACE_DAYS = 7;

/**
 * The enforceable half of MFA - the control a SOC 2 audit asks about.
 * Owner-only. Turning it on stamps the moment, which starts every
 * un-enrolled member's grace period.
 *
 * Deliberately a button behind a confirmation rather than a checkbox: this is
 * the most consequential control in the feature. Turning it on changes how
 * every member of the account signs in and starts a clock after which people
 * who have not enrolled cannot get in at all. Turning it off silently removes
 * that protection from everyone. Neither should be one stray click.
 */
const RequireMfaToggle = observer(() => {
  const { accountStore, uiStore } = useStores();
  const [confirming, setConfirming] = useState(false);
  const account = accountStore.account;
  if (!account) {
    return null;
  }
  if (!account.teamMemberIsOwner) {
    return null;
  }
  const requireMfa = Boolean(account.flags?.requireMfa);
  const saving = uiStore.accountFlagsUpdating.isLoading;

  const apply = async () => {
    const next = !requireMfa;
    try {
      // ONE request, both flags. Two sequential setAccountFlag calls could
      // half-fail into {requireMfa: true, requireMfaSince: null} - enforcement
      // on with no stamp, which the gate reads as grace already over and uses
      // to lock every un-enrolled member out on the spot, the owner included.
      // null rather than undefined when clearing: JSON.stringify drops
      // undefined, so the server's merge would keep the old stamp.
      await accountStore.setAccountFlags({
        requireMfaSince: next ? new Date().toISOString() : null,
        requireMfa: next,
      });
      setConfirming(false);
    } catch (err) {
      // the store has already put local state back, so the screen shows what
      // it showed before the attempt. That is not a guarantee the server
      // agrees - a request that committed but whose response was lost would
      // leave the two out of step until the next load - so say what happened
      // and leave the dialog open rather than closing as though it worked.
      toastError({
        message: (err as Error).message || "Could not change the requirement.",
        extra: err,
      });
    }
  };

  return (
    <div className="mt-4" data-testid="require-mfa-toggle">
      <div className="d-flex align-items-center flex-wrap">
        <Button
          color={requireMfa ? "outline-danger" : "primary"}
          size="sm"
          disabled={saving}
          data-testid="require-mfa-button"
          onClick={() => setConfirming(true)}
        >
          {requireMfa
            ? "Turn off two-factor requirement"
            : "Require two-factor authentication"}
        </Button>
        <span className="text-muted ml-3" data-testid="require-mfa-status">
          {requireMfa
            ? `Two-factor authentication is required for the whole account. Members without it have ${MFA_GRACE_DAYS} days from when you turned this on.`
            : `Activate two-factor authentication for your whole account. Users will have ${MFA_GRACE_DAYS} days to complete.`}
        </span>
      </div>

      {confirming && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirming(false)}
          header={
            requireMfa
              ? "Turn off the two-factor requirement?"
              : "Require two-factor authentication?"
          }
          body={
            requireMfa ? (
              <>
                Members will no longer need two-factor authentication to sign
                in. Anyone who has already set it up keeps it, but nobody new
                will be asked to.
                <br />
                <br />
                This removes a protection from everyone on the account.
              </>
            ) : (
              <>
                Everyone on this team will need two-factor authentication to
                sign in.
                <br />
                <br />
                Members who have not set it up get{" "}
                <strong>{MFA_GRACE_DAYS} days</strong> to do so. After that they
                cannot use Encharge until they enrol. If someone loses access to
                their authenticator, you can reset their two-factor
                authentication from their row in the table below.
              </>
            )
          }
          buttons={[
            {
              label: "Cancel",
              onClick: () => setConfirming(false),
              color: "link",
            },
            {
              label: requireMfa ? "Turn it off" : "Require it",
              onClick: apply,
              color: requireMfa ? "danger" : "primary",
              loading: saving,
              // explicit "button": ConfirmDialog gives a primary button
              // type="submit", and its <Form onSubmit> ALSO calls the primary
              // button's onClick - so leaving this to default fires the action
              // twice. A deliberate click is right for this control anyway.
              type: "button",
            },
          ]}
        />
      )}
    </div>
  );
});

export default RequireMfaToggle;
